import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card } from 'primereact/card';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Chart } from 'primereact/chart';
import { Toast } from 'primereact/toast';
import { Message } from 'primereact/message';
import dayjs from 'dayjs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import ChartJS from 'chart.js/auto';
import { LogisticsGood, LogisticsGoodContactHistory, LogisticsGoodPriceHistory } from '../types';
import { logisticsGoodsAPI } from '../services/api';

type PdfSection = 'filters' | 'summary' | 'compare' | 'prices' | 'contacts' | 'chart' | 'ranking';

const monthLabels = ['Jan', 'Feb', 'Mar', 'Apr', 'Maj', 'Jun', 'Jul', 'Avg', 'Sep', 'Okt', 'Nov', 'Dec'];
const formatMonth = (month: number, year: number) => `${monthLabels[month - 1]} ${year}.`;

const formatCurrency = (n: number) => Number(n).toFixed(2);

const slugify = (s: string) =>
  (s || '')
    .toLowerCase()
    .trim()
    .replaceAll('š', 's')
    .replaceAll('đ', 'dj')
    .replaceAll('č', 'c')
    .replaceAll('ć', 'c')
    .replaceAll('ž', 'z')
    .replaceAll(/[^a-z0-9]+/g, '-')
    .replaceAll(/^-+|-+$/g, '');

type PriceRow = {
  supplier: string;
  month: number;
  year: number;
  price: number;
  currency: string;
};

type ContactRow = {
  supplier: string;
  date: string;
  notes: string;
};

type SupplierStatsRow = {
  supplier: string;
  lastPrice: number | null;
  lastCurrency: string;
  minPrice: number | null;
  maxPrice: number | null;
  avgPrice: number | null;
  updatedLabel: string;
  entries: number;
};

const isValidDateRange = (from: Date | null, to: Date | null) => {
  if (!from && !to) return true;
  if (!from || !to) return false;
  return !dayjs(to).isBefore(dayjs(from), 'day');
};

const inMonthRange = (month: number, year: number, from: Date | null, to: Date | null) => {
  if (!from || !to) return true; // "Bez vremenskog perioda"
  const d = dayjs(new Date(year, month - 1, 1)).startOf('month');
  const f = dayjs(from).startOf('day');
  const t = dayjs(to).endOf('day');
  return d.valueOf() >= f.valueOf() && d.valueOf() <= t.valueOf();
};

const renderLineChartImage = async (labels: string[], dataset: { label: string; data: Array<number | null>; color: string }) => {
  const canvas = document.createElement('canvas');
  canvas.width = 1200;
  canvas.height = 420;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  const chart = new ChartJS(ctx, {
    type: 'line',
    data: {
      labels,
      datasets: [
        {
          label: dataset.label,
          data: dataset.data,
          borderColor: dataset.color,
          backgroundColor: dataset.color,
          tension: 0.35,
          fill: false,
          spanGaps: true,
          pointRadius: 4,
          pointHoverRadius: 6
        }
      ]
    },
    options: {
      responsive: false,
      animation: false,
      plugins: {
        legend: { display: true, position: 'bottom' }
      },
      scales: {
        y: { ticks: { callback: (v: string | number) => `${v}` } }
      }
    } as any
  });

  chart.update();
  const url = canvas.toDataURL('image/png', 1.0);
  chart.destroy();
  return url;
};

const renderBarChartImage = async (labels: string[], datasets: any[]) => {
  const canvas = document.createElement('canvas');
  canvas.width = 1400;
  canvas.height = 520;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  const chart = new ChartJS(ctx, {
    type: 'bar',
    data: {
      labels,
      datasets
    },
    options: {
      responsive: false,
      animation: false,
      plugins: {
        legend: { display: true, position: 'bottom' }
      },
      scales: {
        x: { stacked: false },
        y: { stacked: false, beginAtZero: true }
      }
    } as any
  });

  chart.update();
  const url = canvas.toDataURL('image/png', 1.0);
  chart.destroy();
  return url;
};

const ProcurementStatistics: React.FC = () => {
  const toast = useRef<Toast>(null);
  const chartRef = useRef<any>(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [goods, setGoods] = useState<LogisticsGood[]>([]);

  const [goodId, setGoodId] = useState<string>('');
  const [supplierNames, setSupplierNames] = useState<string[]>([]);

  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);

  const [pdfSections, setPdfSections] = useState<PdfSection[]>(['filters', 'summary', 'compare', 'prices', 'contacts', 'chart', 'ranking']);
  const [supplierForPdf, setSupplierForPdf] = useState<string>('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const gds = await logisticsGoodsAPI.getAll();
      setGoods(gds);
    } catch (e: any) {
      // eslint-disable-next-line no-console
      console.error(e);
      setError(e?.response?.data?.message || 'Greška pri učitavanju podataka');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const goodsOptions = useMemo(() => goods.map((g) => ({ label: g.name, value: g._id })), [goods]);

  const selectedGood = useMemo(() => goods.find((g) => g._id === goodId) || null, [goods, goodId]);

  const availableSupplierNames = useMemo(() => {
    const set = new Set<string>();
    (selectedGood?.suppliers || []).forEach((s) => {
      if (s?.name) set.add(s.name);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [selectedGood]);

  const supplierOptions = useMemo(() => availableSupplierNames.map((n) => ({ label: n, value: n })), [availableSupplierNames]);

  useEffect(() => {
    // keep supplier selections valid when the good changes
    setSupplierNames((prev) => prev.filter((n) => availableSupplierNames.includes(n)));
    setSupplierForPdf((prev) => (availableSupplierNames.includes(prev) ? prev : ''));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goodId, availableSupplierNames.join('|')]);

  const periodLabel = useMemo(() => {
    if (!fromDate || !toDate) return 'Bez vremenskog perioda';
    return `${fromDate.toLocaleDateString('sr-RS')} - ${toDate.toLocaleDateString('sr-RS')}`;
  }, [fromDate, toDate]);

  const effectiveSupplierSet = useMemo(() => {
    const chosen = supplierNames.length ? supplierNames : availableSupplierNames;
    return new Set(chosen);
  }, [supplierNames, availableSupplierNames]);

  const priceRows: PriceRow[] = useMemo(() => {
    if (!selectedGood) return [];
    const out: PriceRow[] = [];
    (selectedGood.suppliers || []).forEach((s) => {
      if (!effectiveSupplierSet.has(s.name)) return;
      (s.priceHistory || []).forEach((p: LogisticsGoodPriceHistory) => {
        if (!inMonthRange(p.month, p.year, fromDate, toDate)) return;
        out.push({ supplier: s.name, month: p.month, year: p.year, price: Number(p.price), currency: p.currency });
      });
    });
    out.sort((a, b) => {
      if (a.supplier !== b.supplier) return a.supplier.localeCompare(b.supplier);
      if (a.year !== b.year) return b.year - a.year;
      return b.month - a.month;
    });
    return out;
  }, [selectedGood, effectiveSupplierSet, fromDate, toDate]);

  const maxPriceByMonthKey = useMemo(() => {
    const map = new Map<string, number>();
    priceRows.forEach((r) => {
      const k = `${r.year}-${String(r.month).padStart(2, '0')}`;
      const prev = map.get(k);
      if (prev === undefined || r.price > prev) map.set(k, r.price);
    });
    return map;
  }, [priceRows]);

  const contactRows: ContactRow[] = useMemo(() => {
    if (!selectedGood) return [];
    const out: ContactRow[] = [];
    (selectedGood.suppliers || []).forEach((s) => {
      if (!effectiveSupplierSet.has(s.name)) return;
      (s.contactHistory || []).forEach((c: LogisticsGoodContactHistory) => {
        out.push({ supplier: s.name, date: c.date, notes: c.notes || '' });
      });
    });
    out.sort((a, b) => {
      if (a.supplier !== b.supplier) return a.supplier.localeCompare(b.supplier);
      return new Date(b.date).getTime() - new Date(a.date).getTime();
    });
    return out;
  }, [selectedGood, effectiveSupplierSet]);

  const supplierStats: SupplierStatsRow[] = useMemo(() => {
    const bySupplier = new Map<string, PriceRow[]>();
    priceRows.forEach((r) => bySupplier.set(r.supplier, [...(bySupplier.get(r.supplier) || []), r]));

    const rows: SupplierStatsRow[] = [];
    Array.from(bySupplier.keys())
      .sort((a, b) => a.localeCompare(b))
      .forEach((supplier) => {
        const rowsForSupplier = (bySupplier.get(supplier) || []).slice().sort((a, b) => (a.year === b.year ? b.month - a.month : b.year - a.year));
        const prices = rowsForSupplier.map((r) => r.price);
        const last = rowsForSupplier[0] || null;
        const min = prices.length ? Math.min(...prices) : null;
        const max = prices.length ? Math.max(...prices) : null;
        const avg = prices.length ? prices.reduce((s, v) => s + v, 0) / prices.length : null;
        rows.push({
          supplier,
          lastPrice: last ? last.price : null,
          lastCurrency: last ? last.currency : 'RSD',
          minPrice: min,
          maxPrice: max,
          avgPrice: avg,
          updatedLabel: last ? formatMonth(last.month, last.year) : '-',
          entries: prices.length
        });
      });
    return rows;
  }, [priceRows]);

  const summary = useMemo(() => {
    const suppliersCount = supplierStats.length;
    const allPrices = priceRows.map((p) => p.price);
    const lowest = allPrices.length ? Math.min(...allPrices) : null;
    const avg = allPrices.length ? allPrices.reduce((s, v) => s + v, 0) / allPrices.length : null;
    const latest = priceRows.length ? priceRows.slice().sort((a, b) => (a.year === b.year ? b.month - a.month : b.year - a.year))[0] : null;
    return {
      suppliersCount,
      lowest,
      avg,
      lastUpdated: latest ? formatMonth(latest.month, latest.year) : '-'
    };
  }, [supplierStats, priceRows]);

  const rankingRows = useMemo(() => {
    const rows = supplierStats
      .filter((r) => r.avgPrice !== null)
      .slice()
      .sort((a, b) => Number(a.avgPrice) - Number(b.avgPrice))
      .map((r, idx) => ({
        rank: idx + 1,
        supplier: r.supplier,
        avgPrice: r.avgPrice as number,
        entries: r.entries
      }));
    return rows;
  }, [supplierStats]);

  const pdfSectionOptions = useMemo(
    () => [
      { label: 'Filteri', value: 'filters' as const },
      { label: 'Rezime', value: 'summary' as const },
      { label: 'Poređenje dobavljača', value: 'compare' as const },
      { label: 'Istorija cena', value: 'prices' as const },
      { label: 'Istorija kontakata', value: 'contacts' as const },
      { label: 'Grafikon', value: 'chart' as const },
      { label: 'Rang lista', value: 'ranking' as const }
    ],
    []
  );

  const resetFilters = () => {
    setGoodId('');
    setSupplierNames([]);
    setSupplierForPdf('');
    setFromDate(null);
    setToDate(null);
    setPdfSections(['filters', 'summary', 'compare', 'prices', 'contacts', 'chart', 'ranking']);
  };

  const getChartData = useMemo(() => {
    if (!selectedGood) return null;
    const monthsSet = new Set<string>();
    priceRows.forEach((p) => monthsSet.add(`${p.year}-${String(p.month).padStart(2, '0')}`));
    const monthKeys = Array.from(monthsSet).sort((a, b) => a.localeCompare(b));
    const labels = monthKeys.map((k) => {
      const [y, m] = k.split('-').map(Number);
      return formatMonth(m, y);
    });

    const colors = ['#2563eb', '#7c3aed', '#06b6d4', '#ef4444', '#22c55e', '#f97316', '#0ea5e9', '#a855f7'];
    const datasets = Array.from(effectiveSupplierSet)
      .sort((a, b) => a.localeCompare(b))
      .map((supplier, idx) => {
        const map = new Map<string, PriceRow>();
        priceRows
          .filter((r) => r.supplier === supplier)
          .forEach((r) => map.set(`${r.year}-${String(r.month).padStart(2, '0')}`, r));
        const color = colors[idx % colors.length];
        return {
          label: supplier,
          data: monthKeys.map((k) => (map.has(k) ? Number(map.get(k)!.price) : null)),
          backgroundColor: color,
          borderColor: color,
          borderWidth: 0,
          borderRadius: 6,
          barPercentage: 0.9,
          categoryPercentage: 0.8
        };
      });

    return { labels, datasets, monthKeys };
  }, [selectedGood, priceRows, effectiveSupplierSet]);

  const chartOptions = useMemo(
    () => ({
      plugins: {
        legend: { position: 'bottom' as const }
      },
      interaction: { mode: 'index' as const, intersect: false },
      scales: {
        x: { stacked: false },
        y: { stacked: false, beginAtZero: true, title: { display: true, text: 'Cena' } }
      }
    }),
    []
  );

  const ensureValidForPdf = () => {
    if (!selectedGood) {
      toast.current?.show({ severity: 'warn', summary: 'Validacija', detail: 'Izaberite robu', life: 2500 });
      return false;
    }
    if (!isValidDateRange(fromDate, toDate)) {
      toast.current?.show({ severity: 'warn', summary: 'Validacija', detail: 'Vremenski period nije validan', life: 2500 });
      return false;
    }
    return true;
  };

  const exportOverviewPdf = async () => {
    if (!ensureValidForPdf() || !selectedGood) return;

    const sections = new Set<PdfSection>(pdfSections.length ? pdfSections : ['filters', 'summary', 'compare', 'prices', 'contacts', 'chart', 'ranking']);

    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
    const margin = 40;
    const pageWidth = doc.internal.pageSize.getWidth();

    const title = 'Izveštaj statistike dobavljača';
    doc.setFontSize(18);
    doc.text(title, pageWidth / 2, 52, { align: 'center' });
    doc.setFontSize(11);
    doc.text(`Roba: ${selectedGood.name}`, pageWidth / 2, 72, { align: 'center' });
    doc.setFontSize(9);
    doc.text(`Generisano: ${new Date().toLocaleString('sr-RS')}`, pageWidth / 2, 88, { align: 'center' });

    let y = 110;

    const sectionHeader = (label: string, color: [number, number, number]) => {
      doc.setFillColor(color[0], color[1], color[2]);
      doc.rect(margin, y, pageWidth - margin * 2, 18, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(10);
      doc.text(label, margin + 8, y + 13);
      doc.setTextColor(0, 0, 0);
      y += 28;
    };

    if (sections.has('filters')) {
      sectionHeader('Filteri', [44, 127, 184]);
      const suppliersLabel = Array.from(effectiveSupplierSet).join(', ') || '-';
      autoTable(doc, {
        startY: y,
        theme: 'grid',
        styles: { fontSize: 9 },
        head: [['Polje', 'Vrednost']],
        body: [
          ['Roba', selectedGood.name],
          ['Dobavljači', suppliersLabel || '-'],
          ['Period', periodLabel]
        ],
        margin: { left: margin, right: margin }
      });
      // @ts-ignore
      y = (doc as any).lastAutoTable.finalY + 18;
    }

    if (sections.has('summary')) {
      sectionHeader('Rezime', [44, 127, 184]);
      autoTable(doc, {
        startY: y,
        theme: 'grid',
        styles: { fontSize: 9 },
        head: [['Ukupno dobavljača', 'Najniža cena', 'Prosečna cena', 'Poslednje ažuriranje']],
        body: [
          [
            String(summary.suppliersCount),
            summary.lowest === null ? '-' : formatCurrency(summary.lowest),
            summary.avg === null ? '-' : formatCurrency(summary.avg),
            summary.lastUpdated
          ]
        ],
        margin: { left: margin, right: margin }
      });
      // @ts-ignore
      y = (doc as any).lastAutoTable.finalY + 18;
    }

    if (sections.has('compare')) {
      sectionHeader('Poređenje dobavljača', [44, 127, 184]);
      autoTable(doc, {
        startY: y,
        theme: 'grid',
        styles: { fontSize: 9 },
        head: [['Dobavljač', 'Poslednja cena', 'Najniža', 'Najviša', 'Prosečna', 'Ažurirano', 'Broj unosa']],
        body: supplierStats.map((r) => [
          r.supplier,
          r.lastPrice === null ? '-' : `${formatCurrency(r.lastPrice)} ${r.lastCurrency}`,
          r.minPrice === null ? '-' : formatCurrency(r.minPrice),
          r.maxPrice === null ? '-' : formatCurrency(r.maxPrice),
          r.avgPrice === null ? '-' : formatCurrency(r.avgPrice),
          r.updatedLabel,
          String(r.entries)
        ]),
        margin: { left: margin, right: margin }
      });
      // @ts-ignore
      y = (doc as any).lastAutoTable.finalY + 18;
    }

    if (sections.has('prices')) {
      sectionHeader('Istorija cena', [46, 125, 50]);
      const maxByMonthKey = new Map<string, number>();
      priceRows.forEach((r) => {
        const k = `${r.year}-${String(r.month).padStart(2, '0')}`;
        const prev = maxByMonthKey.get(k);
        if (prev === undefined || r.price > prev) maxByMonthKey.set(k, r.price);
      });
      autoTable(doc, {
        startY: y,
        theme: 'grid',
        styles: { fontSize: 9 },
        head: [['Dobavljač', 'Period', 'Cena']],
        body: priceRows.map((r) => [r.supplier, formatMonth(r.month, r.year), `${formatCurrency(r.price)} ${r.currency}`]),
        didParseCell: (data) => {
          if (data.section !== 'body') return;
          const rowIndex = data.row.index;
          const r = priceRows[rowIndex];
          if (!r) return;
          const k = `${r.year}-${String(r.month).padStart(2, '0')}`;
          const max = maxByMonthKey.get(k);
          if (max !== undefined && r.price === max) {
            data.cell.styles.fillColor = [254, 226, 226]; // red-100
            data.cell.styles.textColor = [153, 27, 27]; // red-800
          }
        },
        margin: { left: margin, right: margin }
      });
      // @ts-ignore
      y = (doc as any).lastAutoTable.finalY + 18;
    }

    if (sections.has('contacts')) {
      sectionHeader('Istorija kontakata', [125, 63, 175]);
      autoTable(doc, {
        startY: y,
        theme: 'grid',
        styles: { fontSize: 9 },
        head: [['Dobavljač', 'Datum', 'Beleška']],
        body: contactRows.map((r) => [r.supplier, new Date(r.date).toLocaleDateString('sr-RS'), r.notes || '']),
        margin: { left: margin, right: margin }
      });
      // @ts-ignore
      y = (doc as any).lastAutoTable.finalY + 18;
    }

    if (sections.has('chart')) {
      sectionHeader('Grafikon poređenja cena', [44, 127, 184]);
      const offscreenImg = getChartData ? await renderBarChartImage(getChartData.labels, getChartData.datasets) : null;
      const canvas = (chartRef.current?.chart?.canvas as HTMLCanvasElement | undefined) || undefined;
      const img = offscreenImg || (canvas ? canvas.toDataURL('image/png', 1.0) : null);
      if (img && canvas) {
        const w = pageWidth - margin * 2;
        const h = Math.min(260, (w * canvas.height) / canvas.width);
        if (y + h > doc.internal.pageSize.getHeight() - 60) {
          doc.addPage();
          y = margin;
        }
        doc.addImage(img, 'PNG', margin, y, w, h);
        y += h + 14;
      } else if (img) {
        const w = pageWidth - margin * 2;
        const h = 280;
        if (y + h > doc.internal.pageSize.getHeight() - 60) {
          doc.addPage();
          y = margin;
        }
        doc.addImage(img, 'PNG', margin, y, w, h);
        y += h + 14;
      } else {
        doc.setFontSize(10);
        doc.text('Grafikon nije dostupan.', margin, y);
        y += 16;
      }
    }

    if (sections.has('ranking')) {
      sectionHeader('Dobavljači od najniže ka najvišoj prosečnoj ceni', [44, 127, 184]);
      autoTable(doc, {
        startY: y,
        theme: 'grid',
        styles: { fontSize: 9 },
        head: [['Br.', 'Dobavljač', 'Prosečna cena', 'Broj zapisa']],
        body: rankingRows.map((r) => [String(r.rank), r.supplier, `${formatCurrency(r.avgPrice)} RSD`, String(r.entries)]),
        margin: { left: margin, right: margin }
      });
    }

    doc.save(`statistics-overview-${slugify(selectedGood.name)}-${Date.now()}.pdf`);
  };

  const exportSupplierPdf = async () => {
    if (!ensureValidForPdf() || !selectedGood) return;
    const supplier = supplierForPdf || (supplierNames.length ? supplierNames[0] : availableSupplierNames[0]);
    if (!supplier) {
      toast.current?.show({ severity: 'warn', summary: 'Validacija', detail: 'Izaberite dobavljača za PDF', life: 2500 });
      return;
    }

    const supplierPrices = priceRows.filter((r) => r.supplier === supplier);
    const supplierContacts = contactRows.filter((r) => r.supplier === supplier);

    const monthsSet = new Set<string>();
    supplierPrices.forEach((p) => monthsSet.add(`${p.year}-${String(p.month).padStart(2, '0')}`));
    const monthKeys = Array.from(monthsSet).sort((a, b) => a.localeCompare(b));
    const labels = monthKeys.map((k) => {
      const [y, m] = k.split('-').map(Number);
      return formatMonth(m, y);
    });
    const map = new Map<string, PriceRow>();
    supplierPrices.forEach((r) => map.set(`${r.year}-${String(r.month).padStart(2, '0')}`, r));
    const data = monthKeys.map((k) => (map.has(k) ? Number(map.get(k)!.price) : null));
    const chartImg = await renderLineChartImage(labels, { label: supplier, data, color: '#2563eb' });

    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
    const margin = 40;
    const pageWidth = doc.internal.pageSize.getWidth();

    doc.setFontSize(18);
    doc.text(`Istorija dobavljača - ${supplier}`, pageWidth / 2, 52, { align: 'center' });
    doc.setFontSize(11);
    doc.text(`Period: ${periodLabel}`, pageWidth / 2, 72, { align: 'center' });
    doc.setFontSize(9);
    doc.text(`Generisano: ${new Date().toLocaleString('sr-RS')}`, pageWidth / 2, 88, { align: 'center' });

    let y = 110;

    const sectionHeader = (label: string, color: [number, number, number]) => {
      doc.setFillColor(color[0], color[1], color[2]);
      doc.rect(margin, y, pageWidth - margin * 2, 18, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(10);
      doc.text(label, margin + 8, y + 13);
      doc.setTextColor(0, 0, 0);
      y += 28;
    };

    sectionHeader('Istorija cena', [44, 127, 184]);
    autoTable(doc, {
      startY: y,
      theme: 'grid',
      styles: { fontSize: 9 },
      head: [['Period', 'Cena']],
      body: supplierPrices.map((r) => [formatMonth(r.month, r.year), `${formatCurrency(r.price)} ${r.currency}`]),
      margin: { left: margin, right: margin }
    });
    // @ts-ignore
    y = (doc as any).lastAutoTable.finalY + 18;

    sectionHeader('Istorija kontakata', [234, 24, 96]);
    autoTable(doc, {
      startY: y,
      theme: 'grid',
      styles: { fontSize: 9 },
      head: [['Datum', 'Beleška']],
      body: supplierContacts.map((r) => [new Date(r.date).toLocaleDateString('sr-RS'), r.notes || '']),
      margin: { left: margin, right: margin }
    });
    // @ts-ignore
    y = (doc as any).lastAutoTable.finalY + 18;

    sectionHeader('Grafikon poređenja cena', [44, 127, 184]);
    if (chartImg) {
      const w = pageWidth - margin * 2;
      const h = 260;
      if (y + h > doc.internal.pageSize.getHeight() - 60) {
        doc.addPage();
        y = margin;
      }
      doc.addImage(chartImg, 'PNG', margin, y, w, h);
      y += h + 12;
    } else {
      doc.setFontSize(10);
      doc.text('Grafikon nije dostupan.', margin, y);
      y += 16;
    }

    doc.save(`supplier-history-${slugify(supplier)}-${Date.now()}.pdf`);
  };

  const supplierStatsRowsEmpty = !selectedGood || !supplierStats.length;

  const cards = (
    <div className="grid mt-2">
      <div className="col-12 md:col-3">
        <Card>
          <div className="text-600">Ukupno dobavljača</div>
          <div style={{ fontSize: 26, fontWeight: 800 }}>{summary.suppliersCount}</div>
        </Card>
      </div>
      <div className="col-12 md:col-3">
        <Card>
          <div className="text-600">Najniža cena</div>
          <div style={{ fontSize: 26, fontWeight: 800 }}>{summary.lowest === null ? '-' : formatCurrency(summary.lowest)}</div>
        </Card>
      </div>
      <div className="col-12 md:col-3">
        <Card>
          <div className="text-600">Prosečna cena</div>
          <div style={{ fontSize: 26, fontWeight: 800 }}>{summary.avg === null ? '-' : formatCurrency(summary.avg)}</div>
        </Card>
      </div>
      <div className="col-12 md:col-3">
        <Card>
          <div className="text-600">Poslednje ažuriranje</div>
          <div style={{ fontSize: 20, fontWeight: 800 }}>{summary.lastUpdated}</div>
        </Card>
      </div>
    </div>
  );

  return (
    <div className="p-4">
      <Toast ref={toast} />

      <div className="mb-4">
        <h2 className="m-0">Statistika dobavljača</h2>
        <div className="text-600 mt-1">Poredi dobavljače, prati kretanje cena i pregledaj istoriju kontakata.</div>
      </div>

      {error && <Message severity="error" text={error} className="mb-3" />}

      <Card className="mb-3">
        <div className="grid align-items-end">
          <div className="col-12 md:col-4">
            <div className="field mb-2">
              <label>Roba</label>
              <Dropdown value={goodId} options={goodsOptions} onChange={(e) => setGoodId(e.value)} appendTo="self" filter showClear placeholder="Izaberite robu" className="w-full" />
            </div>
          </div>
          <div className="col-12 md:col-8">
            <div className="field">
              <label>Dobavljači</label>
              <MultiSelect
                value={supplierNames}
                options={supplierOptions}
                onChange={(e) => setSupplierNames(e.value || [])}
                appendTo="self"
                filter
                showClear
                display="chip"
                placeholder="Svi dobavljači"
                style={{ width: '100%', position: 'relative' }}
                disabled={!selectedGood}
              />
            </div>
          </div>

          <div className="col-12 md:col-3">
            <div className="field">
              <label>Početni datum</label>
              <Calendar value={fromDate} onChange={(e) => setFromDate(e.value as Date)} dateFormat="dd.mm.yy" showIcon appendTo="self" className="w-full" />
            </div>
          </div>
          <div className="col-12 md:col-3">
            <div className="field">
              <label>Krajnji datum</label>
              <Calendar value={toDate} onChange={(e) => setToDate(e.value as Date)} dateFormat="dd.mm.yy" showIcon appendTo="self" className="w-full" />
            </div>
          </div>

          <div className="col-12 md:col-3">
            <div className="field">
              <label>Sadržaj PDF-a</label>
              <MultiSelect
                value={pdfSections}
                options={pdfSectionOptions}
                onChange={(e) => setPdfSections(e.value || [])}
                appendTo="self"
                display="chip"
                filter
                placeholder="Izaberite sadržaj"
                style={{ width: '100%', position: 'relative' }}
              />
            </div>
          </div>
          <div className="col-12 md:col-3">
            <Button label="Izvezi pregled (PDF)" icon="pi pi-file-pdf" severity="danger" className="w-full" onClick={exportOverviewPdf} loading={loading} />
          </div>

          <div className="col-12 md:col-6">
            <div className="field">
              <label>Istorija dobavljača</label>
              <Dropdown
                value={supplierForPdf}
                options={supplierOptions}
                onChange={(e) => setSupplierForPdf(e.value)}
                appendTo="self"
                filter
                showClear
                placeholder="Izaberite dobavljača"
                disabled={!selectedGood}
              />
            </div>
          </div>
          <div className="col-12 md:col-6">
            <div className="flex gap-2 justify-content-end">
              <Button label="Resetuj filtere" icon="pi pi-refresh" className="p-button-text" onClick={resetFilters} />
              <Button label="Izvezi istoriju dobavljača" icon="pi pi-file" severity="danger" onClick={exportSupplierPdf} loading={loading} />
            </div>
          </div>
        </div>
      </Card>

      {selectedGood ? (
        <>
          {cards}

          <div className="grid mt-2">
            <div className="col-12">
              <Card title="Poređenje dobavljača">
                <DataTable value={supplierStats} emptyMessage="Nema podataka" className="p-datatable-sm" paginator rows={10} responsiveLayout="stack" breakpoint="768px">
                  <Column field="supplier" header="Dobavljač" sortable />
                  <Column
                    header="Poslednja cena"
                    body={(r: SupplierStatsRow) => (r.lastPrice === null ? '-' : `${formatCurrency(r.lastPrice)} ${r.lastCurrency}`)}
                    sortable
                    style={{ width: '170px' }}
                  />
                  <Column header="Najniža cena" body={(r: SupplierStatsRow) => (r.minPrice === null ? '-' : `${formatCurrency(r.minPrice)} RSD`)} sortable style={{ width: '150px' }} />
                  <Column header="Najviša cena" body={(r: SupplierStatsRow) => (r.maxPrice === null ? '-' : `${formatCurrency(r.maxPrice)} RSD`)} sortable style={{ width: '150px' }} />
                  <Column header="Prosečna cena" body={(r: SupplierStatsRow) => (r.avgPrice === null ? '-' : `${formatCurrency(r.avgPrice)} RSD`)} sortable style={{ width: '160px' }} />
                  <Column field="updatedLabel" header="Ažurirano" style={{ width: '150px' }} />
                  <Column field="entries" header="Broj unosa" style={{ width: '120px' }} />
                </DataTable>
              </Card>
            </div>
          </div>

          <div className="grid mt-2">
            <div className="col-12">
              <Card title="Istorija cena">
                <DataTable
                  value={priceRows}
                  emptyMessage="Nema podataka o cenama"
                  className="p-datatable-sm"
                  paginator
                  rows={15}
                  rowsPerPageOptions={[15, 30, 50]}
                  rowClassName={(r: PriceRow) => {
                    const k = `${r.year}-${String(r.month).padStart(2, '0')}`;
                    const max = maxPriceByMonthKey.get(k);
                    return max !== undefined && r.price === max ? 'price-row-max' : '';
                  }}
                  responsiveLayout="stack"
                  breakpoint="768px"
                >
                  <Column field="supplier" header="Dobavljač" sortable />
                  <Column header="Period" body={(r: PriceRow) => formatMonth(r.month, r.year)} sortable />
                  <Column header="Cena" body={(r: PriceRow) => `${formatCurrency(r.price)} ${r.currency}`} style={{ width: '160px' }} sortable />
                </DataTable>
              </Card>
            </div>
          </div>

          <div className="grid mt-2">
            <div className="col-12">
              <Card title="Istorija kontakata">
                <DataTable value={contactRows} emptyMessage="Nema kontakata" className="p-datatable-sm" paginator rows={15} rowsPerPageOptions={[15, 30, 50]} responsiveLayout="stack" breakpoint="768px">
                  <Column field="supplier" header="Dobavljač" sortable />
                  <Column header="Datum" body={(r: ContactRow) => new Date(r.date).toLocaleDateString('sr-RS')} sortable style={{ width: '160px' }} />
                  <Column field="notes" header="Beleška" />
                </DataTable>
              </Card>
            </div>
          </div>

          <div className="grid mt-2">
            <div className="col-12">
              <Card title="Grafikon poređenja cena">
                {getChartData ? (
                  <Chart ref={chartRef} type="bar" data={{ labels: getChartData.labels, datasets: getChartData.datasets }} options={chartOptions} />
                ) : (
                  <div className="text-600">Nema podataka za grafikon.</div>
                )}
              </Card>
            </div>
          </div>

          <div className="grid mt-2">
            <div className="col-12">
              <Card>
                <div className="text-600 mb-2">Dobavljači od najniže ka najvišoj prosečnoj ceni</div>
                {rankingRows.length ? (
                  <div style={{ fontSize: 14 }}>
                    {rankingRows.map((r) => (
                      <div key={r.supplier}>
                        {r.rank}. {r.supplier} - {formatCurrency(r.avgPrice)} RSD · Broj zapisa: {r.entries}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-600">Nema podataka.</div>
                )}
              </Card>
            </div>
          </div>
        </>
      ) : (
        <Message severity="info" text="Izaberite robu da biste videli statistiku." />
      )}

      {supplierStatsRowsEmpty && selectedGood ? <Message severity="warn" text="Nema dovoljno podataka o cenama za izabrane filtere." className="mt-3" /> : null}
      {!isValidDateRange(fromDate, toDate) ? <Message severity="warn" text="Vremenski period nije validan (krajnji datum je pre početnog)." className="mt-3" /> : null}
    </div>
  );
};

export default ProcurementStatistics;


